import { Link } from 'react-router-dom'
import type { ClientAction, PlayerView } from '@sh/shared'
import { DoveIcon, VictoryIcon } from '../../components/Icons.js'
import { Feedback } from '../../components/Feedback.js'

const REASON = {
  liberal_policies: 'Five liberal policies were enacted.',
  fascist_policies: 'Six fascist policies were enacted.',
  hitler_executed: 'Hitler was executed.',
  hitler_chancellor: 'Hitler was elected Chancellor.',
} as const

export function GameOverPhase({
  view,
  code,
  act,
}: {
  view: PlayerView
  code: string
  act: (a: ClientAction) => void
}) {
  const isHost = view.you?.isHost === true

  return (
    <div className="stack">
      <section className={`card card--result card--${view.winner ?? 'aborted'}`}>
        {/* The same pair of emblems the cinematic and the table screen use, so
            the three places a result is announced are one result. */}
        {view.winner && (
          <span className="result__emblem" aria-hidden="true">
            {view.winner === 'liberal' ? <DoveIcon /> : <VictoryIcon />}
          </span>
        )}
        <h2 className="result__title" data-testid="result">
          {view.winner === 'liberal'
            ? 'Liberals win'
            : view.winner === 'fascist'
              ? 'Fascists win'
              : 'Game abandoned'}
        </h2>
        {view.winReason && <p className="result__reason">{REASON[view.winReason]}</p>}
      </section>

      {view.roles && (
        <section className="card">
          <h2 className="card__title">Everyone</h2>
          <ul className="roster">
            {view.seatOrder.map((id) => {
              const role = view.roles?.[id]
              const player = view.players.find((p) => p.id === id)
              if (!player) return null
              return (
                <li key={id} className={`roster__row roster__row--${role ?? 'new'}`}>
                  <span className="roster__name">{player.name}</span>
                  {role ? (
                    <span className={`badge badge--role badge--${role}`}>
                      {role === 'hitler' ? 'Hitler' : role}
                    </span>
                  ) : (
                    // Arrived after this game ended, so they will be dealt in next.
                    <span className="badge">joined for the next game</span>
                  )}
                </li>
              )
            })}
          </ul>
        </section>
      )}

      {/*
        At a practice table the round that matters next is a real one, so that
        is what this card is about; dealing the bots in again is the second
        thing it offers, not the first.
      */}
      {view.config.practice === true ? (
        <section className="card card--note" data-testid="practice-over">
          <h2 className="card__title">Now play it for real</h2>
          <p className="card__hint">
            That was the whole game, against bots that cannot argue back. At a real table the bots
            are your friends, and the arguing is the game.
          </p>
          <Link className="btn btn--primary btn--block" to="/" data-testid="practice-real">
            Start a table
          </Link>
          <button
            type="button"
            className="btn btn--ghost btn--block"
            onClick={() => act({ type: 'host_return_to_lobby' })}
            data-testid="practice-again"
          >
            Practice again
          </button>
        </section>
      ) : (
    <section className="card card--note">
          <h2 className="card__title">Another round?</h2>
          <p className="card__hint">
            Anyone can join on <strong>{code}</strong> right now — they will be seated for the next
            game. The host takes the table back to the lobby to arrange seats and deal again; the
            reveal mode and skin carry over.
          </p>
          {isHost ? (
            <button
              type="button"
              className="btn btn--primary btn--block"
              onClick={() => act({ type: 'host_return_to_lobby' })}
              data-testid="return-to-lobby"
            >
              Back to the lobby
            </button>
          ) : (
            <p className="waiting">Waiting for the host to set up the next game.</p>
          )}
        </section>
      )}

      {/*
        The moment, and the only one this application offers. The table is
        still together, the argument about the fifth vote is still going, and
        everybody is holding the thing they have an opinion about.

        Last on the screen on purpose. The host has a game to restart and that
        button must not be pushed down the page; everybody else has just been
        told to wait, and this is the one useful thing they can do while they
        do. Which is also why it asks rather than nags.
      */}
      <section className="card card--note">
        <h2 className="card__title">Was it any good?</h2>
        <p className="card__hint">
          Whatever helped tonight, or got in the way, or was not where you went looking for it —
          this is the only way anybody finds out. One box, your words, nothing attached to them.
        </p>
        <Feedback className="btn btn--ghost btn--block">Tell us how it went</Feedback>
      </section>
    </div>
  )
}
